import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { getScenario, createRun, submitDecision } from "../lib/api";
import { useSector } from "../lib/sector";
import KatlasProofCard, { KatlasNodeBadge } from "../components/KatlasProofCard";
import CarExplainer from "../components/CarExplainer";
import EvidencePack from "../components/EvidencePack";
import RbtFacilitation from "../components/RbtFacilitation";
import LearningReflection from "../components/LearningReflection";
import ReadinessDiagnostic from "../components/ReadinessDiagnostic";
import { ActiveSectorPill } from "../components/SectorPicker";
import {
    Stepper,
    StepCard,
    DecisionGate,
    DecisionSummary,
    MiniBlock,
    ContinueButton,
} from "../components/ScenarioParts";

export default function ScenarioPage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const { sectorId, setSectorId } = useSector();

    // Hydrate sector from ?sector= deep-link on mount so external links work.
    useEffect(() => {
        const urlSector = searchParams.get("sector");
        if (urlSector && urlSector !== sectorId) {
            setSectorId(urlSector);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [data, setData] = useState(null);
    const [run, setRun] = useState(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [diagnostic, setDiagnostic] = useState({});
    const [reflection, setReflection] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let alive = true;
        setData(null);
        setRun(null);
        setStepIndex(0);
        setDiagnostic({});
        setReflection("");
        getScenario(slug, sectorId)
            .then((d) => {
                if (!alive) return;
                setData(d);
                const init = {};
                d.diagnostic_categories.forEach((c) => (init[c.key] = 2));
                setDiagnostic(init);
            })
            .catch(() => alive && setData({ error: true }));
        return () => {
            alive = false;
        };
    }, [slug, sectorId]);

    const scenario = data?.scenario;
    const categories = data?.diagnostic_categories || [];
    const roleMap = scenario?.role_map || {};
    const remapRole = (r) => roleMap[r] || r;
    const currentStep = scenario?.flow?.[stepIndex];

    const ensureRun = async () => {
        if (run) return run;
        const r = await createRun(slug, "facilitator", sectorId);
        setRun(r);
        return r;
    };

    const nextStep = async () => {
        await ensureRun();
        setStepIndex((i) => Math.min(i + 1, (scenario?.flow?.length || 1) - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submit = async (outcome) => {
        if (!scenario) return;
        setSubmitting(true);
        try {
            const r = await ensureRun();
            const accountableRole =
                scenario.flow.find((f) => f.decision_required)?.role ||
                scenario.roles[scenario.roles.length - 2];
            const updated = await submitDecision(r.id, {
                decision_id: outcome.id,
                decision_label: outcome.label,
                accountable_role: remapRole(accountableRole),
                diagnostic,
                reflection_notes: reflection,
            });
            setRun(updated);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setSubmitting(false);
        }
    };

    if (!scenario) {
        return (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-slate-500">
                Loading scenario…
            </div>
        );
    }

    return (
        <div data-testid="scenario-page">
            <ScenarioHeader
                scenario={scenario}
                stepIndex={stepIndex}
                done={!!run?.receipt}
                remapRole={remapRole}
            />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    {run?.receipt ? (
                        <DecisionSummary run={run} scenario={scenario} />
                    ) : (
                        <ScenarioBody
                            currentStep={currentStep}
                            stepIndex={stepIndex}
                            total={scenario.flow.length}
                            remapRole={remapRole}
                            outcomes={scenario.decision_outcomes}
                            onSubmit={submit}
                            submitting={submitting}
                            onContinue={nextStep}
                        />
                    )}

                    {run?.receipt && <KatlasProofCard receipt={run.receipt} />}
                    {run?.evidence_pack && <EvidencePack run={run} />}
                </div>

                <ScenarioSidebar scenario={scenario} remapRole={remapRole} />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 space-y-8">
                <RbtFacilitation prompts={scenario.rbt_prompts} />
                <LearningReflection
                    prompts={scenario.reflection_prompts}
                    value={reflection}
                    onChange={setReflection}
                />
                <ReadinessDiagnostic
                    categories={categories}
                    values={diagnostic}
                    onChange={setDiagnostic}
                    readOnly={!!run?.receipt}
                />
                <div>
                    <div className="overline mb-3">Governance substrate</div>
                    <CarExplainer />
                </div>
            </div>
        </div>
    );
}

function ScenarioHeader({ scenario, stepIndex, done, remapRole }) {
    return (
        <section className="border-b border-slate-200 bg-slate-50/60">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-10">
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                    <Link to="/" className="overline hover:text-slate-900">
                        ← Back to lab
                    </Link>
                    <span className="w-8 h-px bg-slate-300" />
                    <KatlasNodeBadge />
                    <span className="w-8 h-px bg-slate-300" />
                    <ActiveSectorPill />
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-4xl leading-[1.08]">
                    {scenario.title}
                </h1>
                <p className="mt-4 text-base lg:text-lg text-slate-700 max-w-3xl leading-relaxed">
                    {scenario.tagline}
                </p>
                {scenario.use_case && (
                    <p
                        className="mt-3 text-sm text-slate-600 max-w-3xl leading-relaxed italic"
                        data-testid="sector-use-case"
                    >
                        {scenario.use_case}
                    </p>
                )}
                <Stepper
                    steps={scenario.flow}
                    active={stepIndex}
                    done={done}
                    remapRole={remapRole}
                />
            </div>
        </section>
    );
}

function ScenarioBody({
    currentStep,
    stepIndex,
    total,
    remapRole,
    outcomes,
    onSubmit,
    submitting,
    onContinue,
}) {
    return (
        <>
            <StepCard
                step={currentStep}
                index={stepIndex}
                total={total}
                remapRole={remapRole}
            />
            {currentStep?.decision_required ? (
                <DecisionGate
                    outcomes={outcomes}
                    onSubmit={onSubmit}
                    submitting={submitting}
                />
            ) : (
                <ContinueButton onClick={onContinue} />
            )}
        </>
    );
}

function ScenarioSidebar({ scenario, remapRole }) {
    return (
        <aside className="space-y-6">
            <div className="tile rounded-sm p-6">
                <div className="overline mb-2">What KATLAS proves here</div>
                <p className="text-sm text-slate-700 leading-relaxed">
                    {scenario.what_katlas_proves}
                </p>
            </div>
            {scenario.evidence_examples && (
                <div
                    className="surface-subtle rounded-sm border-l-4 border-slate-900 p-6"
                    data-testid="sector-evidence-examples"
                >
                    <div className="overline mb-2 text-slate-700">
                        Sector evidence examples
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">
                        {scenario.sector?.label}
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-800">
                        {scenario.evidence_examples.map((e) => (
                            <li key={e} className="flex gap-2">
                                <span className="text-slate-400">·</span>
                                <span>{e}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="tile rounded-sm p-6">
                <div className="overline mb-3">Roles in this scenario</div>
                <ol className="space-y-2 text-sm">
                    {scenario.roles.map((r, i) => (
                        <li key={r} className="flex items-center gap-2 text-slate-800">
                            <span className="font-mono text-[11px] text-slate-500 w-6">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            {remapRole(r)}
                        </li>
                    ))}
                </ol>
            </div>
            <div className="tile rounded-sm p-6">
                <div className="overline mb-3">CAR map for this scenario</div>
                <MiniBlock label="Custody" value={scenario.car_mapping.custody} />
                <MiniBlock label="Authority" value={scenario.car_mapping.authority} />
                <MiniBlock label="Receipts" value={scenario.car_mapping.receipts} />
            </div>
        </aside>
    );
}
